import React, { useState, useEffect } from 'react';
import { Plus, RefreshCw, Eye, Edit3, Trash2 } from 'lucide-react';
import { 
  Layout, 
  SearchBar, 
  Pagination, 
  FilterDropdown, 
  ConfirmDialog, 
  LoadingSpinner,
  Alert,
  EmptyState,
  FilterOption
} from '../../components';
import { usePagination, useDebounce, useFilters } from '../../hooks';
import { resourceService } from '../../services';
import { Resource, RESOURCE_TYPE_LABELS } from '../../types';
import { logger } from '../../utils';
import ResourceDetailView from './ResourceDetailView';
import ResourceEditView from './ResourceEditView';

const ResourcesPage: React.FC = () => {
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'detail' | 'edit' | 'create'>('list');
  const [showFilters, setShowFilters] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<Resource | null>(null);
  const [availableTypes, setAvailableTypes] = useState<{type: string; count: number}[]>([]);
  
  // Use custom hooks
  const { pagination, setPage, setLimit, setPaginationData } = usePagination(25);
  const { filters, setFilter, clearAllFilters, hasActiveFilters } = useFilters();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 500);

  const fetchResources = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(filters.type && filters.type !== 'all' && { type: filters.type }),
        ...(filters.status && filters.status !== 'all' && { status: filters.status }),
        ...(filters.verified && filters.verified !== 'all' && { verified: filters.verified }),
      };

      const response = await resourceService.getAll(params);
      setResources(response.resources);
      setPaginationData(response.pagination);
    } catch (err) {
      logger.apiError('/admin/resources', err, { component: 'ResourcesPage', action: 'fetchResources' });
      setError('Failed to fetch resources. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats on mount to get available types
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const stats = await resourceService.getStats();
        if (stats.typeBreakdown) {
          setAvailableTypes(stats.typeBreakdown);
        }
      } catch (err) {
        console.error('Failed to fetch resource stats:', err);
      }
    };
    fetchStats();
  }, []);

  useEffect(() => {
    fetchResources();
  }, [pagination.page, pagination.limit, debouncedSearch, filters.type, filters.status, filters.verified]);

  const handleCreateResource = () => {
    setSelectedResource(null);
    setViewMode('create');
  };

  const handleViewResource = (resource: Resource) => {
    setSelectedResource(resource);
    setViewMode('detail');
  };

  const handleEditResource = (resource: Resource) => {
    setSelectedResource(resource);
    setViewMode('edit');
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedResource(null);
    fetchResources();
  };

  const handleDeleteResource = async () => {
    if (!deleteConfirm) return;
    
    try {
      await resourceService.delete(deleteConfirm.id);
      setDeleteConfirm(null);
      fetchResources();
    } catch (err) {
      logger.apiError('/admin/resources/delete', err, { component: 'ResourcesPage', action: 'deleteResource' });
      setError('Failed to delete resource');
      setDeleteConfirm(null);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    clearAllFilters();
    setPage(1);
  };

  const getStatusColor = (isActive: boolean, isVerified: boolean) => {
    if (!isActive) return 'bg-gray-100 text-gray-800';
    if (isVerified) return 'bg-green-100 text-green-800';
    return 'bg-yellow-100 text-yellow-800';
  };

  const getStatusText = (isActive: boolean, isVerified: boolean) => {
    if (!isActive) return 'Inactive';
    if (isVerified) return 'Verified';
    return 'Active';
  };

  // Filter options - dynamically generated from available types in DB
  const typeOptions: FilterOption[] = [
    { label: 'All Types', value: 'all' },
    ...availableTypes.map(({ type, count }) => ({
      label: `${RESOURCE_TYPE_LABELS[type as keyof typeof RESOURCE_TYPE_LABELS] || type} (${count})`,
      value: type,
    })),
  ];

  const statusOptions: FilterOption[] = [
    { label: 'All Status', value: 'all' },
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
  ];

  const verifiedOptions: FilterOption[] = [
    { label: 'All', value: 'all' },
    { label: 'Verified', value: 'true' },
    { label: 'Unverified', value: 'false' },
  ];

  if (viewMode === 'detail' && selectedResource) {
    return (
      <ResourceDetailView 
        resource={selectedResource} 
        onBack={handleBackToList}
        onEdit={() => setViewMode('edit')}
        onVerify={() => {}}
        onStatusChange={() => {}}
      />
    );
  }

  if (viewMode === 'edit' || viewMode === 'create') {
    return (
      <ResourceEditView 
        resource={viewMode === 'edit' ? selectedResource : null}
        onBack={handleBackToList}
        onSave={handleBackToList}
      />
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Resources Management</h1>
            <p className="text-sm text-gray-600 mt-1">
              Manage all community resources and services ({pagination.total} total)
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <button
              onClick={fetchResources}
              disabled={loading}
              className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={handleCreateResource}
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Resource
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search resources by name, address, or contact..."
              className="flex-1"
            />
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Filters {hasActiveFilters && `(${Object.keys(filters).length})`}
            </button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
              <FilterDropdown
                label="Type"
                value={filters.type || 'all'}
                options={typeOptions}
                onChange={(value) => setFilter('type', value)}
                onClear={() => setFilter('type', 'all')}
              />
              
              <FilterDropdown
                label="Status"
                value={filters.status || 'all'}
                options={statusOptions}
                onChange={(value) => setFilter('status', value)}
                onClear={() => setFilter('status', 'all')}
              />
              
              <FilterDropdown
                label="Verification"
                value={filters.verified || 'all'}
                options={verifiedOptions}
                onChange={(value) => setFilter('verified', value)}
                onClear={() => setFilter('verified', 'all')}
              />
              
              <div className="flex items-end">
                <button
                  onClick={handleClearFilters}
                  disabled={!hasActiveFilters && !searchTerm}
                  className="w-full px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Clear All
                </button>
              </div>
            </div>
          )}
        </div>

        {error && (
          <Alert
            variant="error"
            message={error}
            onClose={() => setError(null)}
          />
        )}

        {/* Resources Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {loading ? (
            <LoadingSpinner className="py-12" message="Loading resources..." />
          ) : resources.length === 0 ? (
            <EmptyState
              title="No resources found"
              message={
                debouncedSearch || hasActiveFilters
                  ? 'Try adjusting your search or filters.'
                  : 'No resources have been added yet.'
              }
              action={{
                label: 'Add Resource',
                onClick: handleCreateResource,
              }}
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name & Address
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {resources.map((resource) => (
                      <tr key={resource.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900 truncate max-w-xs">
                              {resource.name}
                            </div>
                            {resource.address && (
                              <div className="text-xs text-gray-500 truncate max-w-xs">
                                {resource.address}
                              </div>
                            )}
                            {resource.city && (
                              <div className="text-xs text-gray-400">
                                {resource.city}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {resource.type}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-xs text-gray-900 space-y-1">
                            {resource.phone && <div className="truncate">{resource.phone}</div>}
                            {resource.email && <div className="truncate">{resource.email}</div>}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(resource.isActive, resource.isVerified)}`}>
                            {getStatusText(resource.isActive, resource.isVerified)}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-1">
                            <button
                              onClick={() => handleViewResource(resource)}
                              className="text-orange-600 hover:text-orange-900 p-1 rounded"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            
                            <button
                              onClick={() => handleEditResource(resource)}
                              className="text-gray-600 hover:text-gray-900 p-1 rounded"
                              title="Edit Resource"
                            >
                              <Edit3 className="h-4 w-4" />
                            </button>

                            <button
                              onClick={() => setDeleteConfirm(resource)}
                              className="text-red-600 hover:text-red-900 p-1 rounded"
                              title="Delete Resource"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.pages}
                totalItems={pagination.total}
                itemsPerPage={pagination.limit}
                onPageChange={setPage}
                onPageSizeChange={setLimit}
                pageSizeOptions={[10, 25, 50, 100]}
                showPageSize={true}
              />
            </>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDeleteResource}
        title="Delete Resource"
        message={`Are you sure you want to delete "${deleteConfirm?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmButtonVariant="danger"
      />
    </Layout>
  );
};

export default ResourcesPage;
